/**
 * Disposition editeur / apercu.
 *
 * En dessous du seuil bureau les deux panneaux sont empiles ; au-dela ils sont
 * cote a cote et separes par une poignee redimensionnable.
 *
 * Cette poignee existait deja dans le code (etat, gestionnaires de souris,
 * styles) mais n'etait rattachee a aucun element : le ratio restait fige et les
 * ecouteurs tournaient en permanence sans effet. Elle est ici reellement
 * branchee, au clavier comme a la souris.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from './hooks/useBreakpoint';
import './ResponsiveHtmlCssLayout.css';

interface ResponsiveHtmlCssLayoutProps {
  editorComponent: React.ReactNode;
  previewComponent: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/** Bornes de la largeur de l'editeur, en pourcentage de la zone. */
const MIN_EDITOR_WIDTH = 25;
const MAX_EDITOR_WIDTH = 75;
const KEYBOARD_STEP = 2;

/** Largeur par defaut de l'editeur selon la place disponible. */
const defaultEditorWidth = (isWide: boolean) => (isWide ? 55 : 62);

const clamp = (value: number) => Math.min(MAX_EDITOR_WIDTH, Math.max(MIN_EDITOR_WIDTH, value));

const ResponsiveHtmlCssLayout: React.FC<ResponsiveHtmlCssLayoutProps> = ({
  editorComponent,
  previewComponent,
  className,
  style,
}) => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const isCompact = breakpoint === 'compact';

  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [editorWidth, setEditorWidth] = useState(() =>
    defaultEditorWidth(breakpoint === 'wide')
  );
  const [dragging, setDragging] = useState(false);

  // Sur un ecran large l'apercu merite plus de place qu'un simple tiers : le
  // ratio fige precedent le laissait tres etroit.
  useEffect(() => {
    setEditorWidth(defaultEditorWidth(breakpoint === 'wide'));
  }, [breakpoint]);

  const updateFromPointer = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0) return;
    setEditorWidth(clamp(((clientX - rect.left) / rect.width) * 100));
  }, []);

  // Les ecouteurs ne sont attaches que pendant un glissement, au lieu de rester
  // en place pour toute la duree de vie du composant.
  useEffect(() => {
    if (!dragging) return;

    const onPointerMove = (event: PointerEvent) => {
      if (!draggingRef.current) return;
      // Empeche la selection de texte pendant le glissement.
      event.preventDefault();
      updateFromPointer(event.clientX);
    };

    const stop = () => {
      draggingRef.current = false;
      setDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [dragging, updateFromPointer]);

  const startDragging = useCallback((event: React.PointerEvent) => {
    event.preventDefault();
    draggingRef.current = true;
    setDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  /** La poignee est aussi manipulable au clavier une fois focalisee. */
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setEditorWidth(current => clamp(current - KEYBOARD_STEP));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setEditorWidth(current => clamp(current + KEYBOARD_STEP));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setEditorWidth(defaultEditorWidth(breakpoint === 'wide'));
    }
  }, [breakpoint]);

  if (isCompact) {
    return (
      <div className={`htmlcss-layout is-compact ${className ?? ''}`} style={style}>
        <div className="htmlcss-layout-editor">{editorComponent}</div>
        <div className="htmlcss-layout-preview">{previewComponent}</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`htmlcss-layout is-split ${dragging ? 'is-dragging' : ''} ${className ?? ''}`}
      style={style}
    >
      <div className="htmlcss-layout-editor" style={{ width: `${editorWidth}%` }}>
        {editorComponent}
      </div>

      <div
        className="htmlcss-layout-handle"
        onPointerDown={startDragging}
        onKeyDown={handleKeyDown}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('htmlCss.layout.resize')}
        aria-valuenow={Math.round(editorWidth)}
        aria-valuemin={MIN_EDITOR_WIDTH}
        aria-valuemax={MAX_EDITOR_WIDTH}
        tabIndex={0}
      />

      <div className="htmlcss-layout-preview" style={{ width: `${100 - editorWidth}%` }}>
        {previewComponent}
      </div>
    </div>
  );
};

export default ResponsiveHtmlCssLayout;
